import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  createFiuEntryApi,
  fiuListApi,
  getAccountBalancesStatusApi,
  getAccountControlLogsApi,
  getAccountControlAssetInrPriceApi,
  lockAccountBalanceApi,
  unlockAccountBalanceApi,
  updateAccountControlStatusApi,
  getAccountControlRequestsApi,
  reviewAccountControlRequestApi,
  requestLockUnlockAccountBalanceApi,
} from "../../utils/api";

export const actionCreateEntryFiu = createAsyncThunk(
  "fiu/createFiuEntry",
  async (data) => {
    const response = await createFiuEntryApi(data);
    return response;
  }
);

export const actionFiuList = createAsyncThunk("fiu/fiuListApi", async () => {
  const response = await fiuListApi({});
  return response;
});

export const actionFetchAccountBalancesStatus = createAsyncThunk(
  "fiu/fetchAccountBalancesStatus",
  async ({ accountId, walletType = "trading" }, { rejectWithValue }) => {
    try {
      const response = await getAccountBalancesStatusApi(accountId);
      return {
        data: response.data,
        walletType,
        accountId,
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const actionFetchAccountControlLogs = createAsyncThunk(
  "fiu/fetchAccountControlLogs",
  async (filters, { rejectWithValue }) => {
    try {
      const {
        email = "",
        assetSymbol = "",
        actionType = "",
        startDate = "",
        endDate = "",
        page = 0,
        pageSize = 10,
      } = filters;
      const response = await getAccountControlLogsApi({
        email,
        assetSymbol,
        actionType,
        startDate,
        endDate,
        page,
        pageSize,
      });
      return { data: response.data, filters: { ...filters, page, pageSize } };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const actionFetchAssetInrPrices = createAsyncThunk(
  "fiu/fetchAssetInrPrices",
  async (assets, { rejectWithValue }) => {
    try {
      const priceMap = {};
      await Promise.all(
        assets.map(async (asset) => {
          if (asset === "INR") {
            priceMap[asset] = 1;
            return;
          }
          try {
            const { data } = await getAccountControlAssetInrPriceApi(asset);
            priceMap[asset] = parseFloat(data?.asset_inr_price || 0);
          } catch {
            priceMap[asset] = 0;
          }
        })
      );
      return priceMap;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const actionRequestLockUnlockAccountBalance = createAsyncThunk(
  "fiu/lockUnlockAccountBalance",
  async ({ entries, accountId, remarks, mode }, { rejectWithValue }) => {
    try {
        const actionType = mode === "block" ? "LOCK_BALANCE" : "UNLOCK_BALANCE";

      for (const entry of entries) {
        const payload = {
          account_id: accountId,
          wallet_type: entry.wallet_type,
          wallet_id: String(entry.wallet_id),
          action_type: actionType,
          remarks,
        };

        if (entry.input_mode === "inr") {
          payload.inr_amount = String(entry.inr_value);
        } else {
          payload.amount = String(entry.asset_quantity);
        }

        await requestLockUnlockAccountBalanceApi(payload);
      }

      return { mode };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const actionRequestUpdateAccountControlStatus = createAsyncThunk(
  "fiu/updateAccountControlStatus",
  async ({ accountId, action : action_type, remarks }, { rejectWithValue }) => {
    try {
      const response = await requestLockUnlockAccountBalanceApi({
        account_id: accountId,
        action_type,
        remarks,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const actionFetchAccountControlRequests = createAsyncThunk(
  "fiu/fetchAccountControlRequests",
  async (filters, { rejectWithValue }) => {
    try {
      const {
        email = "",
        accountId = "",
        assetSymbol = "",
        actionType = "",
        requestedBy = "",
        status = "",
        page = 0,
        pageSize = 10,
      } = filters;
      const response = await getAccountControlRequestsApi({
        email,
        accountId,
        assetSymbol,
        actionType,
        requestedBy,
        status,
        page,
        pageSize,
      });
      return { data: response.data, filters: { ...filters, page, pageSize } };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const actionReviewAccountControlRequest = createAsyncThunk(
  "fiu/reviewAccountControlRequest",
  async ({ id, decision, reviewRemarks }, { rejectWithValue }) => {
    try {
      const payload = {
        decision,
        review_remarks: reviewRemarks,
      };
      const { data } = await reviewAccountControlRequestApi(id, payload);
      return data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);


export const actionLockUnlockAccountBalance = createAsyncThunk(
  "fiu/lockUnlockAccountBalance",
  async ({ entries, accountId, remarks, mode }, { rejectWithValue }) => {
    try {
      const apiFn =
        mode === "block" ? lockAccountBalanceApi : unlockAccountBalanceApi;

      for (const entry of entries) {
        const payload = {
          account_id: accountId,
          wallet_type: entry.wallet_type,
          wallet_id: String(entry.wallet_id),
          remarks,
        };

        if (entry.input_mode === "inr") {
          payload.inr_amount = String(entry.inr_value);
        } else {
          payload.amount = String(entry.asset_quantity);
        }

        await apiFn(payload);
      }

      return { mode };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const actionUpdateAccountControlStatus = createAsyncThunk(
  "fiu/updateAccountControlStatus",
  async ({ accountId, action, remarks }, { rejectWithValue }) => {
    try {
      const response = await updateAccountControlStatusApi({
        account_id: accountId,
        action,
        remarks,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);